import {
  Button,
  Drawer,
  Form,
  Input,
  message,
  Radio,
  Select,
  Switch,
} from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import React, { useEffect, useState } from 'react';
import { BankAccountsService } from 'services/BankAccountsService';
import { BVUService } from 'services/BVUService';
import {
  iinRule,
  onlyNumberRule,
  requiredRule,
} from 'utils/forms/validationRules';
import { currencyList } from 'utils/handbooks/bankAccountsHandbooks';

function BankAccountsForm({
  isActive,
  onClose,
  onSuccess,
  initialValues,
  defaultIin = null,
}) {
  const [form] = Form.useForm();
  const [banks, setBanks] = useState([]);
  const isEditing = initialValues !== null;

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    if (initialValues) {
      const { account, active, bik, bin_iin, name_ru, code, currency } =
        initialValues;

      form.setFieldsValue({
        account,
        active,
        bik,
        bin_iin,
        name_ru,
        code,
        currency,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  useEffect(() => {
    const defaultIinValue = defaultIin ?? '';

    form.setFieldsValue({
      bin_iin: defaultIinValue,
    });
  }, [defaultIin]);

  const fetchBanks = async () => {
    try {
      const banks = await BVUService.getAllBanks();
      setBanks(banks);
    } catch (err) {
      message.error('Не удалось подгрузить все банки');
    }
  };

  const handleBankChange = (bank) => {
    const selectedBank = banks.find((b) => b.name === bank);

    if (selectedBank) {
      const { bik, code } = selectedBank;

      form.setFieldsValue({
        bik,
        code,
      });
    }
  };

  const handleSubmit = (values) => {
    if (isEditing) {
      handleUpdate(values, initialValues.id);
    } else {
      handleCreate(values);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const createdBankAccount = await BankAccountsService.createBankAccount(
        data
      );

      if (createdBankAccount) {
        handleConfirm(createdBankAccount);
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await BankAccountsService.updateBankAccount(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = (account) => {
    onClose();
    onSuccess(account);
    form.resetFields();
  };

  const resetBankOptions = () => {
    form.resetFields(['bik', 'code']);
  };

  return (
    <Drawer
      title={
        isEditing
          ? 'Редактирование банковского счёта'
          : 'Создание банковского счёта'
      }
      width="50%"
      onClose={onClose}
      open={isActive}
      zIndex={1002}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item
          name="account"
          label="Номер счёта"
          rules={[
            requiredRule,
            {
              len: 20,
              message: 'Номер должен состоять из 20 символов',
            },
          ]}
        >
          <Input placeholder="Счёт" maxLength={20} allowClear />
        </Form.Item>

        <Form.Item
          name="bin_iin"
          label="ИИН/БИН"
          rules={[requiredRule, onlyNumberRule, iinRule]}
        >
          <Input
            allowClear
            maxLength={12}
            placeholder="Укажите БИН/ИИН"
            readOnly={defaultIin || isEditing}
          />
        </Form.Item>

        <Form.Item
          name="name_ru"
          label="Наименование банка"
          rules={[requiredRule]}
        >
          <Select
            allowClear
            onChange={handleBankChange}
            placeholder="Банк"
            showSearch
            optionFilterProp="children"
            onClear={resetBankOptions}
          >
            {banks.map(({ id, name }) => (
              <Select.Option key={id} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="code" label="Код">
          <Input placeholder="Код" readOnly />
        </Form.Item>

        <Form.Item name="bik" label="БИК">
          <Input placeholder="БИК" readOnly />
        </Form.Item>

        <Form.Item name="currency" label="Валюта" rules={[requiredRule]}>
          <Radio.Group>
            {currencyList.map(({ text, value }) => (
              <Radio.Button value={value}>{text}</Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item name="active" label="Активный" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { BankAccountsForm };
